import { useState, useMemo, useContext, useEffect } from 'react';
import { ChevronLeft, Volume2, VolumeX } from 'lucide-react';
import shuffleArray from '../../utils/shuffleArray';
import { UserContext } from '../../services/user/LoginUserContext';
import * as Tone from 'tone';
import { Quiz, Question, UserAnswer } from '../../types';
import { logger } from '../../utils/logger';
import { useQuizSoundOn } from '../../services/QuizSoundOnService'; // インポートを更新

interface QuizViewProps {
  quiz: Quiz;
  onQuizComplete: (quiz: Quiz, userAnswers: UserAnswer[]) => void;
  showMessage: (message: string, onConfirm: () => void, onCancel: () => void) => void;
}

const QuizView: React.FC<QuizViewProps> = ({ quiz, onQuizComplete, showMessage }) => {
  const { uid, loading } = useContext(UserContext);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswer[]>([]);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [soundOn, setSoundOn] = useQuizSoundOn(); // useQuizSoundOn を使用

  const questions: Question[] = quiz.questions;

  const synth = useMemo(() => new Tone.PolySynth(Tone.Synth).toDestination(), []);
  const correctFeedback = useMemo(() => new Tone.MembraneSynth().toDestination(), []);
  const incorrectFeedback = useMemo(() => new Tone.NoiseSynth({
    noise: { type: "pink" },
    envelope: { attack: 0.005, decay: 0.1, sustain: 0.05, release: 0.2 },
  }).toDestination(), []);

  useEffect(() => {
    const startAudio = () => {
      if (Tone.context.state !== 'running') {
        Tone.start();
        logger.log('Tone.js audio context started.');
      }
      document.removeEventListener('click', startAudio);
      document.removeEventListener('keydown', startAudio);
    };

    document.addEventListener('click', startAudio);
    document.addEventListener('keydown', startAudio);

    return () => {
      document.removeEventListener('click', startAudio);
      document.removeEventListener('keydown', startAudio);
    };
  }, []);

  const shuffledOptions: string[] = useMemo(() => {
    if (!questions[currentQuestionIndex]) return [];
    return shuffleArray([...questions[currentQuestionIndex].options]);
  }, [currentQuestionIndex, questions]);

  if (loading) {
    return <div>読み込み中...</div>;
  }

  if (!uid) {
    return <div>クイズを受けるにはログインしてください。</div>;
  }

  const handleAnswer = async (option: string) => {
    if (selectedOption) return;
    const correct = option === questions[currentQuestionIndex].correctAnswer;
    setSelectedOption(option);
    setIsCorrect(correct);

    const currentQuestion = questions[currentQuestionIndex];

    if (soundOn) {
      if (correct) {
        correctFeedback.triggerAttackRelease("C4", "8n");
      } else {
        incorrectFeedback.triggerAttackRelease("8n");
      }
    }

    const answerToSave: Omit<UserAnswer, 'answeredAt'> = {
      ...currentQuestion,
      answer: option,
      correct: correct,
      isReview: quiz.isReview,
      userId: uid,
      quizId: quiz.id,
    };

    setUserAnswers((prevAnswers) => [...prevAnswers, answerToSave as UserAnswer]);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOption(null);
      setIsCorrect(null);
    } else {
      setShowResult(true);
    }
  };

  if (showResult) {
    const score = userAnswers.filter((a) => a.correct).length;
    const total = questions.length;
    return (
      <div className="p-4 sm:p-8 max-w-2xl mx-auto bg-white rounded-2xl shadow-xl text-center">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">クイズ結果</h2>
        <p className="text-5xl font-extrabold text-transparent bg-clip-text bg-custom-gradient from-sky-500 to-cyan-400 my-6">
          {score} / {total}
        </p>
        <button
          onClick={() => onQuizComplete(quiz, userAnswers)}
          className="py-3 px-10 bg-custom-gradient from-violet-500 to-purple-500 text-white rounded-full font-bold text-lg shadow-lg hover:from-violet-600 hover:to-purple-600 transform hover:scale-105 transition-all"
        >
          ホームにもどる
        </button>
      </div>
    );
  }

  const currentQuestion: Question = questions[currentQuestionIndex];

  return (
    <div className="p-4 sm:p-8 max-w-2xl mx-auto bg-white rounded-2xl shadow-xl">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => showMessage('クイズを中断しますか？', () => onQuizComplete(quiz, userAnswers), () => { })}
          className="text-gray-500 hover:text-gray-800"
        >
          <ChevronLeft size={28} />
        </button>
        <div className="text-lg font-bold text-gray-700">
          {currentQuestionIndex + 1} / {questions.length}
        </div>
        <label className="flex items-center cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              className="sr-only"
              checked={soundOn}
              onChange={(e) => setSoundOn(e.target.checked)}
            />
            <div
              className={`block w-10 h-5 rounded-full transition border-2 ${
                soundOn ? 'bg-blue-500 border-blue-600' : 'bg-gray-500 border-gray-600'
              }`}
            ></div>
            <div
              className={`dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-300 ease-in-out shadow-md ${
                soundOn ? 'translate-x-5' : 'translate-x-0'
              }`}
              style={{
                transform: soundOn ? 'translateX(1.25rem)' : 'translateX(0)',
              }}
            ></div>
          </div>
          {soundOn ? (
            <Volume2 className="ml-2 text-blue-500" size={16} />
          ) : (
            <VolumeX className="ml-2 text-gray-500" size={16} />
          )}
        </label>
      </div>
      <div className="bg-gray-100 p-6 rounded-lg mb-6">
        <p className="text-lg text-gray-800 font-semibold whitespace-pre-wrap">{currentQuestion.questionText}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {shuffledOptions.map((option: string, index: number) => {
          const isSelected = selectedOption === option;
          const isCorrectAnswer = option === currentQuestion.correctAnswer;
          let buttonClass = 'bg-white hover:bg-sky-100 border-2 border-sky-400 text-sky-700';
          if (selectedOption) {
            if (isCorrectAnswer) {
              buttonClass = 'bg-green-500 border-green-500 text-white';
            } else if (isSelected) {
              buttonClass = 'bg-red-500 border-red-500 text-white';
            } else {
              buttonClass = 'bg-gray-200 border-gray-200 text-gray-500 opacity-60';
            }
          }
          return (
            <button
              key={index}
              onClick={() => handleAnswer(option)}
              disabled={!!selectedOption}
              className={`p-4 rounded-lg font-semibold text-left transition duration-200 w-full shadow-sm ${buttonClass}`}
            >
              {option}
            </button>
          );
        })}
      </div>
      {selectedOption && (
        <div
          className={`mt-6 p-4 rounded-lg animate-fade-in-up ${isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
        >
          <h4 className="font-bold text-lg mb-2">{isCorrect ? '正解！' : '残念！'}</h4>
          <p>{currentQuestion.explanation}</p>
          <div className="flex justify-end mt-4">
            <button
              onClick={handleNextQuestion}
              className="py-2 px-6 bg-custom-gradient from-sky-500 to-cyan-500 text-white rounded-full font-bold shadow-lg hover:scale-105 transition-transform"
            >
              {currentQuestionIndex < questions.length - 1 ? '次の問題へ →' : '結果を見る'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizView;